<template>
  <v-form ref="form" @input="onChange">
    <v-row>
      <v-col cols="12">
        <h3 class="mb-4">Certifikat za fiskalizaciju</h3>
        <p class="text-body-2 mb-4">
          Upload certifikata je opcionalan. Bez certifikata možete koristiti samo Transakcijske blagajne.
          Možete preskočiti ovaj korak i dodati certifikat naknadno.
        </p>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-file-input
            dense
            outlined
            label="Učitaj certifikat"
            accept=".p12, .pfx, .pem, .crt, .cer, .ca-bundle, .p7b, .p7c, .p7s"
            :rules="[rules.certSize]"
            @change="certFileChange"
            hint="Podržani formati: .p12, .pfx, .pem, .crt, .cer"
            persistent-hint
            clearable
          />
          <field-help-dialog
            title="Certifikat za fiskalizaciju"
            description="Digitalni certifikat izdan od strane Fina e-potpisivanje koji omogućuje fiskalizaciju računa putem Porezne uprave. Potreban je za korištenje Gotovinskih i Mješovitih blagajni."
            :required="false"
            format="Podržani formati: .p12, .pfx, .pem, .crt, .cer, .ca-bundle, .p7b, .p7c, .p7s (max 1MB)"
            example="firma_certifikat_2024.p12"
            notes="Certifikat možete nabaviti putem Fina e-Građani servisa. Ako ga trenutno nemate, možete preskočiti ovaj korak i koristiti samo Transakcijske blagajne (računovodstvene evidentne). Certifikat možete dodati kasnije u postavkama firme."
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Lozinka certifikata"
            v-model="certificatePassword"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showPassword = !showPassword"
            :disabled="!certificate"
            :rules="certificate ? [rules.req] : []"
            hide-details="auto"
          />
          <field-help-dialog
            title="Lozinka certifikata"
            description="Lozinka koja štiti vaš digitalni certifikat. Ova lozinka je postavljena prilikom kreiranja certifikata kod Fina e-potpisivanja."
            :required="false"
            notes="Ovo polje postaje obavezno tek kada učitate certifikat. Lozinka se sigurno pohranjuje i koristi samo za fiskalizaciju računa. Ako ste zaboravili lozinku, morat ćete kreirati novi certifikat kod Fina."
          />
        </div>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <v-alert type="info" dense outlined text>
          <div style="line-height: 1.6;">
            <strong>Važna napomena:</strong> Certifikat za fiskalizaciju je potreban za korištenje Gotovinskih i Mješovitih blagajni. Ako ga ne dodate sada, kasnije možete dodati u postavkama firme.
          </div>
        </v-alert>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import rules from '@/plugins/rules'
import FieldHelpDialog from '../shared/FieldHelpDialog.vue'

export default {
  name: 'StepCertificate',
  components: {
    FieldHelpDialog
  },
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      certificate: null,
      certificatePassword: '',
      showPassword: false,
      rules: {
        req: rules.req(),
        certSize: (value) =>
          !value ||
          value.size < 1048487 ||
          'Najveća dozvoljena veličina dokumenta je 1MB.'
      }
    }
  },
  watch: {
    certificate (newVal) {
      this.$emit('input', {
        certificate: newVal,
        password: this.certificatePassword
      })
    },
    certificatePassword (newVal) {
      this.$emit('input', {
        certificate: this.certificate,
        password: newVal
      })
    }
  },
  methods: {
    validate () {
      // Validacija je uspješna samo ako je certifikat odabran I lozinka unesena
      // Ili ako nije odabran certifikat (opcionalno)
      if (!this.certificate) {
        return true // Opcionalno, nema certifikata
      }

      if (this.certificate && this.certificatePassword) {
        return this.$refs.form.validate()
      }

      return false
    },
    onChange () {
      this.$emit('change')
    },
    toBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },
    async certFileChange (_file) {
      if (_file) {
        try {
          const file = await this.toBase64(_file)
          this.certificate = file.split(',')[1]
        } catch (err) {
          this.certificate = null
          this.certificatePassword = ''
        }
      } else {
        this.certificate = null
        this.certificatePassword = ''
      }
    },
    getData () {
      if (this.certificate && this.certificatePassword) {
        return {
          certificate: this.certificate,
          password: this.certificatePassword
        }
      }
      return null
    },
    hasCertificate () {
      return !!(this.certificate && this.certificatePassword)
    }
  },
  mounted () {
    // Inicijaliziraj s vrijednostima iz props ako postoje
    if (this.value && Object.keys(this.value).length > 0) {
      this.certificate = this.value.certificate || null
      this.certificatePassword = this.value.password || ''
    }
  }
}
</script>

<style scoped>
h3 {
  color: #1577da;
}
</style>
